import { Col, DatePicker, Form, Input, message, Modal, Popconfirm, Row } from "antd";
import { Rule } from "antd/lib/form";
import { customerApi } from "api/customer.api";
import { staffApi } from "api/staff.api";
import { workScheduleApi } from "api/workSchedule.api";
import { AppButton, AppSelect } from "components/antd-ui";
import { CommentThread } from "components/Comment/CommentThread";
import { CommentRefType } from "types/comment";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { userStore } from "store/userStore";
import { Customer } from "types/customer";
import { Staff } from "types/staff";
import { WorkSchedule } from "types/workSchedule";
import "components/SplitModal/SplitModal.scss";
import { can } from "utils/auth";
import dayjs, { Dayjs } from "utils/dayjs";

const schedulePath = "/work-schedule";
const requiredRules: Rule[] = [{ required: true, message: "Bắt buộc" }];

type Option = { label: string; value: number };
type CustomerOption = Option & { address?: string };

interface ScheduleForm {
  title: string;
  range?: [Dayjs, Dayjs];
  staffId?: number;
  customerId?: number;
  address?: string;
  goal?: string;
  result?: string;
}

export interface WorkScheduleModal {
  handleCreate: (date: Dayjs) => void;
  handleUpdate: (schedule: WorkSchedule) => void;
}

interface WorkScheduleModalProps {
  onSubmitOk: () => void;
}

export const WorkScheduleModal = observer(
  React.forwardRef(({ onSubmitOk }: WorkScheduleModalProps, ref) => {
    const [form] = Form.useForm<ScheduleForm>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [selected, setSelected] = useState<WorkSchedule>();
    const [staffOptions, setStaffOptions] = useState<Option[]>([]);
    const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);

    const onlyMine = !userStore.info.role?.isAdmin && can(schedulePath, "ONLY_MINE");
    const isUpdate = !!selected;
    const readOnly = isUpdate ? !can(schedulePath, "UPDATE") : false;
    const canDelete = isUpdate && can(schedulePath, "DELETE");

    const fetchStaff = useCallback(
      async (keyword: string) => {
        const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
        const staffs: Staff[] = res.data.staffs || [];
        const options = staffs.map((staff) => ({ label: staff.name || staff.username, value: staff.id }));
        setStaffOptions((prev) => {
          const selectedOption = prev.find((option) => option.value === form.getFieldValue("staffId"));
          return selectedOption && !options.some((option) => option.value === selectedOption.value)
            ? [selectedOption, ...options]
            : options;
        });
      },
      [form],
    );

    const fetchCustomer = useCallback(
      async (keyword: string) => {
        const res = await customerApi.findAll({ page: 1, limit: 20, search: keyword });
        const customers: Customer[] = res.data.customers || [];
        const options = customers.map((customer) => ({
          label: customer.name,
          value: customer.id,
          address: customer.address,
        }));
        setCustomerOptions((prev) => {
          const selectedOption = prev.find((option) => option.value === form.getFieldValue("customerId"));
          return selectedOption && !options.some((option) => option.value === selectedOption.value)
            ? [selectedOption, ...options]
            : options;
        });
      },
      [form],
    );

    const debounceSearchStaff = useMemo(() => debounce((keyword: string) => fetchStaff(keyword), 500), [fetchStaff]);
    const debounceSearchCustomer = useMemo(
      () => debounce((keyword: string) => fetchCustomer(keyword), 500),
      [fetchCustomer],
    );

    useEffect(
      () => () => {
        debounceSearchStaff.cancel();
        debounceSearchCustomer.cancel();
      },
      [debounceSearchStaff, debounceSearchCustomer],
    );

    useEffect(() => {
      if (!visible) return;
      if (!onlyMine) fetchStaff("");
      fetchCustomer("");
    }, [visible, onlyMine, fetchStaff, fetchCustomer]);

    useImperativeHandle<any, WorkScheduleModal>(
      ref,
      () => ({
        handleCreate(date: Dayjs) {
          form.resetFields();
          form.setFieldsValue({ range: [date.hour(9).minute(0).second(0), date.hour(17).minute(0).second(0)] });
          setStaffOptions(
            userStore.info.id ? [{ label: userStore.info.name || "", value: userStore.info.id }] : [],
          );
          if (userStore.info.id) form.setFieldsValue({ staffId: userStore.info.id });
          setCustomerOptions([]);
          setSelected(undefined);
          setVisible(true);
        },
        handleUpdate(schedule: WorkSchedule) {
          form.setFieldsValue({
            title: schedule.title,
            range: [dayjs.unix(schedule.startAt), dayjs.unix(schedule.endAt)],
            staffId: schedule.staff?.id,
            customerId: schedule.customer?.id,
            address: schedule.address,
            goal: schedule.goal,
            result: schedule.result,
          });
          setStaffOptions(
            schedule.staff ? [{ label: schedule.staff.name || schedule.staff.username, value: schedule.staff.id }] : [],
          );
          setCustomerOptions(schedule.customer ? [{ label: schedule.customer.name, value: schedule.customer.id }] : []);
          setSelected(schedule);
          setVisible(true);
        },
      }),
      [form],
    );

    const handleClose = useCallback(() => {
      setVisible(false);
      setSelected(undefined);
    }, []);

    const handleCustomerChange = useCallback(
      (customerId?: number) => {
        const address = customerOptions.find((option) => option.value === customerId)?.address;
        if (address && !form.getFieldValue("address")) {
          form.setFieldsValue({ address });
        }
      },
      [customerOptions, form],
    );

    const handleSubmit = useCallback(async () => {
      const values = await form.validateFields();
      const [start, end] = values.range as [Dayjs, Dayjs];
      const payload = {
        schedule: {
          title: values.title,
          startAt: start.unix(),
          endAt: end.unix(),
          goal: values.goal || "",
          result: values.result || "",
          address: values.address || "",
        },
        staffId: onlyMine ? undefined : values.staffId,
        customerId: values.customerId || 0,
      };
      setLoading(true);
      try {
        if (selected) {
          await workScheduleApi.update(selected.id, payload);
        } else {
          await workScheduleApi.create(payload);
        }
        message.success(selected ? "Cập nhật thành công!" : "Tạo mới thành công!");
        handleClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    }, [form, handleClose, onSubmitOk, onlyMine, selected]);

    const handleDelete = useCallback(async () => {
      if (!selected) return;
      setLoading(true);
      try {
        await workScheduleApi.delete(selected.id);
        message.success("Đã xóa lịch công tác");
        handleClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    }, [handleClose, onSubmitOk, selected]);

    return (
      <Modal
        open={visible}
        title={isUpdate ? "Lịch công tác" : "Thêm lịch công tác"}
        width={isUpdate ? "min(1100px, 92vw)" : 680}
        style={{ top: 20 }}
        onCancel={handleClose}
        footer={
          <div className="flex justify-between">
            <div>
              {canDelete && (
                <Popconfirm title="Xóa lịch công tác này?" okText="Xóa" cancelText="Hủy" onConfirm={handleDelete}>
                  <AppButton tone="danger" loading={loading}>
                    Xóa
                  </AppButton>
                </Popconfirm>
              )}
            </div>
            <div className="flex gap-2">
              <AppButton onClick={handleClose}>Đóng</AppButton>
              {!readOnly && (
                <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
                  Lưu
                </AppButton>
              )}
            </div>
          </div>
        }
      >
        <div className={`splitModal${isUpdate ? "" : " splitModal--single"}`}>
        <div className="splitModal__col">
        <Form layout="vertical" form={form} disabled={readOnly}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Tiêu đề" name="title" rules={requiredRules}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item label="Thời gian" name="range" rules={requiredRules}>
                <DatePicker.RangePicker
                  showTime={{ format: "HH:mm", minuteStep: 5 }}
                  format="DD/MM/YYYY HH:mm"
                  allowClear={false}
                  style={{ width: "100%" }}
                  placeholder={["Bắt đầu", "Kết thúc"]}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              {onlyMine ? (
                <Form.Item label="Nhân viên">
                  <Input value={userStore.info.name || ""} disabled />
                </Form.Item>
              ) : (
                <Form.Item label="Nhân viên" name="staffId" rules={requiredRules}>
                  <AppSelect
                    showSearch
                    filterOption={false}
                    options={staffOptions}
                    onSearch={debounceSearchStaff}
                    placeholder="Chọn nhân viên"
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={12}>
              <Form.Item label="Khách hàng" name="customerId">
                <AppSelect
                  showSearch
                  allowClear
                  filterOption={false}
                  options={customerOptions}
                  onSearch={debounceSearchCustomer}
                  onChange={handleCustomerChange}
                  placeholder="Chọn khách hàng (không bắt buộc)"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Địa chỉ" name="address">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Mục tiêu" name="goal">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Kết quả" name="result">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        </div>
        {selected && (
          <div className="splitModal__col">
            <h3 className="font-semibold mb-2">Bình luận</h3>
            <CommentThread refType={CommentRefType.WorkSchedule} refId={selected.id} code={selected.title} />
          </div>
        )}
        </div>
      </Modal>
    );
  }),
);
